import _ from 'lodash';
import { Observable, BehaviorSubject, Subscription, merge } from 'rxjs';
import { DynamicControlBaseComponent } from 'app/shared/components/dynamic-control-base/dynamic-control-base.component';
import { switchMap, map } from 'rxjs/operators';
import { ISwitchCase } from '../metadatas/control/select-switch-metadata';
import { ControlMetadata } from '../metadatas/control/control-metadata';
import { IAttribute } from 'app/model/base/annotation';
import { Named } from 'app/model/base/named';
import { stringIsNullOrEmpty } from 'app/shared/utils/string.util';
import { Metamodel } from 'app/model/base/model';
import { IControlsSharedByCases, IRadioSwitchMetadata } from '../metadatas/control/radio-switch-metadata';
import { ISelectGroupedOption, ISelectOption } from 'app/interfaces/ISelectOption';
import { CategoryType, localizeCategoryType } from 'app/shared/utils/connectorCategory.util';
import { ILinkedServiceTypeMetadata } from '../metadatas/resource-type/linked-service-type-metadata';

import { IDataflowFormatTypeMetadata } from 'app/extensibility/metadatas/resource-type/dataflow-format-type-metadata';
import { adfModelTypeMap } from 'app/registries/consts/adf-model-type-map-const';
import { calcExpressionString, isExpressionString } from './expression.util';
import { IPropertyBag } from '../property-bag';

export interface IDynamicControlValueChange {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    controls: Array<DynamicControlBaseComponent<any>>;
    // control and value will only be undefined for the first time
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    control?: DynamicControlBaseComponent<any>;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    value?: any;
}

interface IDynamicControlFlattenResult {
    valueChange$: Observable<IDynamicControlValueChange>;
    subscription: Subscription;
}

// this function will flatten all children including those don't show out in UI
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function flattenAllControls(controls: Array<DynamicControlBaseComponent<any>>): Array<DynamicControlBaseComponent<any>> {
    return controls
        .map((control) => {
            if (!_.isEmpty(control.allChildren)) {
                return [control, ...flattenAllControls(control.allChildren.map((child) => child.instance))];
            } else if (!_.isEmpty(control.children)) {
                return [control, ...flattenAllControls(control.children.map((child) => child.instance))];
            } else {
                return [control];
            }
        })
        .reduce((prev, curr) => prev.concat(curr), []); // Components combined together
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function flattenControls(controls: Array<DynamicControlBaseComponent<any>>): Array<DynamicControlBaseComponent<any>> {
    return controls
        .map((control) =>
            _.isEmpty(control.children) ?
                [control] :
                [control, ...flattenControls(control.children.map((child) => child.instance))] // Existing children for each component (those show out in UI)
        )
        .reduce((prev, curr) => prev.concat(curr), []); // Components combined together
}

/**
 * If any of the value got changed, a valueChange$ will be triggered.
 * There's a subscription in the result, need the caller to unsubscribe it.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getFlattenDynamicControlOnValueChangeObservable(rootControls: Array<DynamicControlBaseComponent<any>>): IDynamicControlFlattenResult {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const controls$ = new BehaviorSubject<Array<DynamicControlBaseComponent<any>>>(flattenControls(rootControls));
    // Use a BehaviorSubject, to ensure the valueChange$ in the output can be always triggered after value change even if the value change also changes the controls$
    // and it can contains the updated controls
    const valueChangeForOutput$ = new BehaviorSubject<IDynamicControlValueChange>({
        controls: controls$.value
    });
    const valueChange$ = controls$
        .pipe(switchMap((controls) =>
            merge(
                ...controls.map((instance) => instance.valueChange.pipe(
                    map((value) => {
                        return {
                            control: instance,
                            value
                        };
                    })))
            )
        ));
    const subscription = valueChange$.subscribe((valueChange) => {
        const newFlattenControls = flattenControls(rootControls);
        if (!_.isEqual(controls$.value, newFlattenControls)) {
            controls$.next(newFlattenControls);
        }
        // The valueChange$ will not be triggered again immediately because the valueChange of each control is not triggered
        valueChangeForOutput$.next({
            control: valueChange.control,
            value: valueChange.value,
            controls: newFlattenControls
        });
    });
    return {
        valueChange$: valueChangeForOutput$,
        subscription
    };
}

export function populateAttributesFromDynamicControl(
    control: ControlMetadata,
    attributesNames?: Set<string>,
    includeAccordionChildren: boolean = false
): Set<string> {
    if (attributesNames == null) {
        attributesNames = new Set();
    }
    if (includeAccordionChildren && control.type === 'accordion' && Array.isArray(control.children)) {
        // The accordion binding is UI state; payload cleanup needs the child property names.
        control.children.forEach((child) => populateAttributesFromDynamicControl(child, attributesNames, true));
        return attributesNames;
    }
    if (control.type === 'parameterizable' || control.type === 'parameterizable-dropdown') {
        attributesNames.add(control.child.bind as string);
    } else if (typeof control.bind === 'string') {
        attributesNames.add(control.bind);
    } else if (typeof control.bind === 'object') {
        Object.keys(control.bind).forEach((key) => attributesNames.add((control.bind as { [key: string]: string })[key]));
    }

    if (control.cases != null && Array.isArray(control.cases)) {
        attributesNames.add(control.bind as string);
        control.cases
            .filter((c) => c.children != null && Array.isArray(c.children))
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            .forEach((c: ISwitchCase<any>) => {
                c.children.forEach((child) => populateAttributesFromDynamicControl(child, attributesNames, includeAccordionChildren));
            });
    }
    if (control.sharedControls != null && Array.isArray(control.sharedControls)) {
        attributesNames.add(control.bind as string);
        control.sharedControls
            .filter((c) => c.controls != null && Array.isArray(c.controls))
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            .forEach((c: IControlsSharedByCases<any>) => {
                c.controls.forEach((control) => populateAttributesFromDynamicControl(control, attributesNames, includeAccordionChildren));
            });
    }
    return attributesNames;
}

/**
 * Recursively convert parameter-wrapped control into raw control
 * @param control Original control
 * @param updateControlInplace Update control in place, if set to true, directly update the control object and return, otherwise clone the control then update
 * @returns Updated control
 */
export function convertParameterControlToPlainControl(control: ControlMetadata, updateControlInplace: boolean = false): ControlMetadata {
    if (control.type === 'parameterizable' && control.child) {
        return {
            ...control.child,
            hide: control.hide
        };
    }

    if (control.type === 'parameterizable-dropdown') {
        return {
            ...control,
            type: 'select'
        };
    }

    if (control.parameterizable) {
        const newControl = updateControlInplace ? control : _.merge({}, control);
        newControl.parameterizable = false;
        return newControl;
    }

    if ((control.cases != null && Array.isArray(control.cases)) || (control.sharedControls != null && Array.isArray(control.sharedControls))) {
        const newControl = updateControlInplace ? control : _.merge({}, control);
        if (newControl.isParameterizable) {
            newControl.isParameterizable = false;
        }

        if (newControl.cases != null) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            newControl.cases = newControl.cases.map((c: ISwitchCase<any>) => {
                if (c != null && Array.isArray(c.children)) {
                    return ({
                        caseValue: c.caseValue,
                        children: c.children.map((child) => convertParameterControlToPlainControl(child))
                    });
                }
                return c;
            });
        }

        if (newControl.sharedControls != null) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            newControl.sharedControls = newControl.sharedControls.map((c: IControlsSharedByCases<any>) => {
                if (c.controls != null && Array.isArray(c.controls)) {
                    return ({
                        caseValues: c.caseValues,
                        controls: c.controls.map((child) => convertParameterControlToPlainControl(child))
                    });
                }
                return c;
            });
        }

        return newControl;
    }

    if (control.type === 'accordion' && control.children != null && Array.isArray(control.children)) {
        const newControl = updateControlInplace ? control : _.merge({}, control);
        newControl.children = newControl.children.map((child) => convertParameterControlToPlainControl(child));
        return newControl;
    }

    return control;
}

/**
 * Recursively remove AKV selection comp in a control
 * @param control Original control
 * @param updateControlInplace Update control in place if set to true, directly update the control object and return, otherwise clone the control then update
 * @returns Updated control
 */
export function convertAKVSwitcherControlToPlainControl(control: ControlMetadata, updateControlInplace: boolean = false): ControlMetadata[] {
    if (control.type === 'dynamic-akv-switcher' && !control.akvOnly && control.bind) {
        const newChildControl = updateControlInplace ? control.child : _.merge({}, control.child);
        if (newChildControl.type === 'parameterizable' && newChildControl.child) {
            newChildControl.child.bind = control.bind;
        } else {
            newChildControl.bind = control.bind;
        }
        return [newChildControl];
    }

    if (control.cases != null && Array.isArray(control.cases)) {
        const newComplexControl = updateControlInplace ? _.merge({}, control) : control;

        if (newComplexControl.cases != null && Array.isArray(newComplexControl.cases)) {
            let caseIndexWithAkvControl;
            if (newComplexControl.cases.length === 2) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                caseIndexWithAkvControl = newComplexControl.cases.findIndex((c: ISwitchCase<any>) => c.children.some((innerItem) => innerItem.type === 'azure-key-vault'));
                if (caseIndexWithAkvControl !== -1) {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    return (newComplexControl.cases[1 - caseIndexWithAkvControl].children as any[]).map((child) => convertAKVSwitcherControlToPlainControl(child))
                        .reduce((pre, cur) => pre.concat(cur), []);
                }
            }
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            newComplexControl.cases = newComplexControl.cases.map((c: ISwitchCase<any>) => {
                if (c != null && Array.isArray(c.children)) {
                    return ({
                        caseValue: c.caseValue,
                        children: c.children.map((child) => convertAKVSwitcherControlToPlainControl(child)).reduce((pre, cur) => pre.concat(cur), [])
                    });
                }
                return c;
            });
        }

        return [newComplexControl];
    }

    return [control];
}

export function adjustControlLabelPositionForCW(control: ControlMetadata): ControlMetadata {
    const newControl = _.merge({}, control);
    if (control.cases != null && Array.isArray(control.cases)) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        newControl.cases = newControl.cases.map((c: ISwitchCase<any>) => {
            if (c != null && Array.isArray(c.children)) {
                return ({
                    caseValue: c.caseValue,
                    children: c.children.map((child) => adjustControlLabelPositionForCW(child))
                });
            }
            return c;
        });
    }
    newControl.labelPosition = 'right';
    if (control.type !== 'checkbox') {
        newControl.isLabelVertical = true;
    }

    return newControl;
}


export function hideControlPreview(control: ControlMetadata): ControlMetadata {
    const newControl = _.merge({}, control);
    if (control.cases != null && Array.isArray(control.cases)) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        newControl.cases = newControl.cases.map((c: ISwitchCase<any>) => {
            if (c != null && Array.isArray(c.children)) {
                return ({
                    caseValue: c.caseValue,
                    children: c.children.map((child) => hideControlPreview(child))
                });
            }
            return c;
        });
    }
    if (newControl.showPreviewButton) {
        newControl.showPreviewButton = false;
    }

    return newControl;
}

export function getAllChildrenControlsOfRadioSwitch(control: IRadioSwitchMetadata): ControlMetadata[] {
    let result: ControlMetadata[] = [];
    if (control != null) {
        const hiddenCases = control.options.filter((opt) => opt.hidden).map((opt) => opt.value);
        control.cases.forEach((c) => {
            if (hiddenCases.indexOf(c.caseValue) < 0) {
                result = result.concat(c.children);
            }
        });
        control.sharedControls.forEach((c) => {
            if (c.caseValues.some((caseValue) => hiddenCases.indexOf(caseValue) < 0)) {
                result = result.concat(c.controls);
            }
        });
    }
    return result;
}

// clean all dynamic attributes value in object
export function cleanupAllDynamicPropertiesInObject(attributeNames: string[], object: {}, excludedProperties?: string[], cleanAllProperties?: boolean): void {
    if (object != null) {
        if (cleanAllProperties) {
            Object.keys(object).forEach((attribute) => {
                // don't clear value of property in excludedProperties
                if (attribute && (!excludedProperties || excludedProperties.indexOf(attribute) === -1) && object) {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    (object as any)[attribute] = undefined;
                }
            });
        } else if (attributeNames) {
            attributeNames.forEach((attribute) => {
                // don't clear value of property in excludedProperties
                if (attribute && (!excludedProperties || excludedProperties.indexOf(attribute) === -1) && object) {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    (object as any)[attribute] = undefined;
                }
            });
        }
    }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getComponentByAttributeName(dynamicControls: Array<DynamicControlBaseComponent<any>>, attribute: string): DynamicControlBaseComponent<any> | undefined {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const scanComponents = (comps: Array<DynamicControlBaseComponent<any>>): DynamicControlBaseComponent<any> | undefined => {
        for (const comp of comps) {
            if (comp == null) {
                continue;
            }
            if (comp.bindPropertyName === attribute) {
                return comp;
            } else if (!comp.bindPropertyName && typeof (comp.metadata.bind) === 'object') {
                if (Object.keys(comp.metadata.bind).some((key) => (comp.metadata.bind as { [key: string]: string })[key] === attribute)) {
                    return comp;
                }
            }
            if (comp.children && comp.children.length > 0) {
                const childrenResult = scanComponents(comp.children.map((child) => child && child.instance));
                if (childrenResult != null) {
                    return childrenResult;
                }
            }
        }
        return undefined;
    };
    return scanComponents(dynamicControls);
}

export function isControlNotUnderFeatureFlag(control: ControlMetadata, propertyBag?: IPropertyBag): boolean {
    const hide =
    typeof control.hide === 'function'
        ? control.hide()
        : isExpressionString(control.hide) && propertyBag
        ? calcExpressionString(control.hide, propertyBag)
        : !!control.hide;
    return !hide;
}

export function isControlNotStatic(control: ControlMetadata): boolean {
    // filter out table-selection control, query mode selection control, for tabular connector,
    // fileformat, file-path-file, compression for file connector, we've statically treat them
    return control.type !== 'table-selection' &&
        !(control.type === 'radio-switch' && control.options && control.options.filter((option) => option.value === 'query').length > 0) &&
        control.type !== 'file-path-file' && control.type !== 'fileformat' && control.type !== 'compression' && control.type !== 'o365-query';
}

export function getAttribute(attributeName: string, entity: Named): IAttribute {
    return entity && !stringIsNullOrEmpty(attributeName) && Metamodel.modelOf(entity) && Metamodel.modelOf(entity).attribute(attributeName);
}

export function calcGroupedConnectionOptions(lsMetadatas: ILinkedServiceTypeMetadata[], allTypeAsSingleItem: boolean = true, allText?: string, disableWorkspace?: boolean): Array<ISelectGroupedOption<string>> {
    const typeResult: Array<ISelectGroupedOption<string>> = [];
    const preResult: { [label: string]: Array<ISelectOption<string>> } = {};
    lsMetadatas.filter((m) => !m.notShowInTypeSelection || m.type === adfModelTypeMap.sqlPool.connectionType).forEach((metadata) => {
        const lsCategories = metadata.category.map((cate) => localizeCategoryType(cate));
        lsCategories.forEach((cate) => {
            const label = metadata.displayName;
            const value = metadata.type;
            const item = {
                label,
                value,
                labelIcon: metadata.icon != null ? metadata.icon.className : ''
            };
            if (preResult[cate]) {
                preResult[cate].push(item);
            } else {
                preResult[cate] = [item];
            }
        });
    });
    let workspaceItem: ISelectGroupedOption<string>;
    Object.keys(preResult).forEach((cate) => {
        if (cate !== stringResources.Workspace) {
            typeResult.push({
                label: cate,
                value: cate,
                items: preResult[cate] || []
            });
        } else {
            workspaceItem = {
                label: cate,
                value: cate,
                items: preResult[cate] || []
            };
        }
    });
    typeResult.sort((a, b) => {
        if (a.label < b.label) {
            return -1;
        } else if (a.label > b.label) {
            return 1;
        }
        return 0;
    });
    if (workspaceItem != null && !disableWorkspace) {
        typeResult.unshift(workspaceItem);
    }

    if (allTypeAsSingleItem) {
        typeResult.unshift({
            label: '',
            items: [{
                value: '',
                label: allText || stringResources.All
            }]
        });
    } else {
        typeResult.unshift({
            label: allText || stringResources.All,
            value: allText || stringResources.All,
            items: typeResult.map((item) => item.items).reduce((pre, cur) => pre.concat(cur), [])
        });
    }
    return typeResult;
}

export const calcGroupedDataflowTypeOptions = (metadatas: IDataflowFormatTypeMetadata[]): Array<ISelectGroupedOption<string>> => {
    const others: ISelectGroupedOption<string> = {
        label: stringResources.Others,
        items: []
    };
    const typeResult: Map<CategoryType, Array<ISelectOption<string>>> = new Map<CategoryType, Array<ISelectOption<string>>>();
    metadatas.forEach(({ displayName: label, type: value, icon: { className: labelIcon }, category: categories }) => {
        const option = { label, value, labelIcon };
        if (categories.length) {
            categories.forEach((category) => {
                if (typeResult.has(category)) {
                    typeResult.get(category).push(option);
                } else {
                    typeResult.set(category, [option]);
                }
            });
        } else {
            others.items.push(option);
        }
    });
    return Array.from(typeResult.entries()).map(([category, options]) => ({
        label: localizeCategoryType(category),
        items: options.sort((a, b) => a.label.localeCompare(b.label))
    }))
        .sort((a, b) => a.label.localeCompare(b.label))
        .concat(others.items.length ? others : []);
};
